import React from 'react';
import { AudioControlContext } from './AudioControl';

export default function AudioControlSeekbar() {
  const {
    isPlaying,
    duration,
    currentPosition,
    doPause,
    doPlay,
    doSeek,
  } = React.useContext(AudioControlContext);

  const [seekX, setSeekX] = React.useState<number|null>(null);
  const [dragging, setDragging] = React.useState(false);
  const knobRef = React.useRef<HTMLSpanElement>(null);
  const barRef = React.useRef<HTMLDivElement>(null);

  const getCurrentProgress = () => {
    if (duration == 0) return 0;
    return currentPosition / duration;
  }

  const getSeekProgress = () => {
    if (seekX === null || !barRef.current) return getCurrentProgress();
    return seekX / barRef.current?.offsetWidth;
  }

  const getMouseSeekX = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): number => {
    const rect = barRef.current?.getBoundingClientRect();
    return e.clientX - (rect ? rect.left : 0);
  };

  const getTouchSeekX = (e: React.TouchEvent<HTMLDivElement>): number => {
    const rect = barRef.current?.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    return touch.clientX - (rect ? rect.left : 0);
  };


  const isSeekXInsideKnob = (currentSeekX: number): boolean => {
    const min = (knobRef.current?.offsetLeft || 0) - (barRef.current?.offsetLeft || 0);
    const max = min + (knobRef.current?.offsetWidth || 0);
    return (min <= currentSeekX && currentSeekX <= max);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (dragging) return;
    setDragging(true);
    setSeekX(getMouseSeekX(e));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!dragging) return;
    if (seekX === null) return;
    setSeekX(getMouseSeekX(e));
  };

  const handleTouchDown = (e: React.TouchEvent<HTMLDivElement>) => {
    if (dragging) return;
    setDragging(true);
    setSeekX(getTouchSeekX(e));
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (seekX === null) return;
    setSeekX(getTouchSeekX(e));
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>|React.TouchEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (seekX === null) return;
    doSeek(getSeekProgress());
    setDragging(false);
    setSeekX(null);
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement, MouseEvent>|React.TouchEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (seekX === null) return;
    doSeek(getSeekProgress());
    setDragging(false);
    setSeekX(null);
  }

  const barProgress = getCurrentProgress() * 100.0;
  const knobProgress = (getSeekProgress()) * 100.0;
  return (
    <div
      className="seek_bar"
      onMouseDown={(e) => { handleMouseDown(e); }}
      onMouseUp={(e) => { handleMouseUp(e); }}
      onMouseMove={(e) => { handleMouseMove(e); }}
      onMouseLeave={(e) => { handleMouseLeave(e); }}
      onTouchStart={(e) => { handleTouchDown(e); }}
      onTouchMove={(e) => { handleTouchMove(e); }}
      onTouchEnd={(e) => { handleMouseUp(e); }}
    >
      <div ref={barRef}><div style={{ width: `${barProgress}%` }} /></div>
      <span ref={knobRef} style={{ left: `${knobProgress}%` }} />
    </div>
  );
}
